import {
  EdgesType,
  hiConsistsOfCustomerOrderBiId,
  IProduct,
  LOCALE_CODES,
  Price,
  OrderItemDiscounts,
  Discount,
  ICorPromoCodes,
  PricPrice,
  DiscountValueType,
  DiscountType,
  TrialInterval,
  DeliveryOption,
  LicenseAgreementInfo,
} from '@global/types/types';
import i18n, { configLanguage } from '@global/localization/i18n';
import { CURRENCY_SYMBOLS } from '@global/App/constants/location/constants';
import { DISCOUNTS } from '@global/App/constants/constants';
import { initOrderPrice } from '@pages/checkout/consts';
import { addDaysUTC, addMonthsUTC, dateFormatter } from '@global/utils/date';
import { getIsDeLocale } from '@src/global/utils/lang';
import { priceFormatter } from '@global/utils/price';
import { SiteConfig } from './SiteConfig';

type PriceType = 'total' | 'tax' | 'price' | 'discount' | 'subtotal';

export type OrderPriceType = Record<PriceType, Price>;

export type OrderItemType = {
  uid: string;
  biiId: string;
  qty: number;
  maName: string;
  isbn13: string;
  numberOfPages: string;
  printOnDemand: boolean;
  publicationDate: string;
  maId?: string;
  imageUrl?: string;
  authors?: string[];
  parentId: string;
  parentCategoryName?: string;
  price: Price;
  totalPrice?: {
    code: string;
    value: number;
  };
  tax?: {
    value: number;
  };
  format?: string;
  category: string;
  categoryId: string;
  priceBeforeDiscount?: number;
  isTextbook: boolean;
  licenseAgreementInfo?: LicenseAgreementInfo;
};

export type PreviewDeliveryOption = {
  id: string;
  title: string;
  price: string;
  isFreeShipping?: boolean;
  description: string;
  date: {
    from: string;
    to: string;
  };
};

export const getImgUrlByProduct = (
  product: Pick<IProduct, 'imageUrl' | 'parents'>
) => {
  return (
    product.parents[0]?.node.wImages.find((i) => i.wFormat === 'normal')
      ?.atUrl ||
    product.imageUrl ||
    ''
  );
};

const getPriceByCode = (arr: EdgesType[], code: string) => {
  return arr?.find((item) => item.mapsOn.code === code)?.node?.amount || 0;
};

const getSymbolByCode = (arr: EdgesType[], code: string) => {
  const SymbolCode = arr?.find((item) => item.mapsOn.code === code)?.node?.units
    ?.code;

  return SymbolCode ? CURRENCY_SYMBOLS[SymbolCode] : '';
};

const getUnitsByCode = (arr: EdgesType[], code: string) => {
  const SymbolCode = arr?.find((item) => item.mapsOn.code === code)?.node?.units
    ?.code;

  return SymbolCode ? SymbolCode : '';
};

export const getOrderPrice = (serverPrices: EdgesType[]): OrderPriceType => {
  if (!serverPrices) return initOrderPrice;
  return {
    tax: {
      value: getPriceByCode(serverPrices, 'TAX'),
      code: getSymbolByCode(serverPrices, 'TAX'),
    },
    total: {
      value: getPriceByCode(serverPrices, 'TOTAL'),
      code: getSymbolByCode(serverPrices, 'TOTAL'),
      units: getUnitsByCode(serverPrices, 'TOTAL'),
    },
    price: {
      value: getPriceByCode(serverPrices, 'PRICE'),
      code: getSymbolByCode(serverPrices, 'PRICE'),
    },
    discount: {
      value: getPriceByCode(serverPrices, 'DISCOUNT'),
      code: getSymbolByCode(serverPrices, 'DISCOUNT'),
    },
    subtotal: {
      value: getPriceByCode(serverPrices, 'SUBTOTAL'),
      code: getSymbolByCode(serverPrices, 'SUBTOTAL'),
    },
  };
};

export const getPriceValue = (amount: number, vat: number): number =>
  amount + (amount / 100) * vat;

export const getOrderItems = (
  arr: hiConsistsOfCustomerOrderBiId[],
  language?: LOCALE_CODES,
  isSubscription?: boolean
): OrderItemType[] => {
  const vat = configLanguage[language].VAT;
  const isDe = getIsDeLocale();

  return arr?.map((item) => {
    const prices = getOrderPrice(item.pricPrice.edges);
    const qty = item.biiQuantity?.amount;
    const priceCode = CURRENCY_SYMBOLS[prices.total.units] || '';

    const parentNode = item.biiSalable.parents?.[0]?.node;

    const authors = parentNode?.wAuthors?.map(({ wName }) => wName);
    const isTextbook = parentNode?.wTextbook || false;
    const imageUrl = getImgUrlByProduct(item.biiSalable);
    const category = item.biiSalable.maCategories?.[0];

    const orderItem: OrderItemType = {
      qty,
      authors,
      uid: item.biiSalable.uid,
      biiId: item.biiId,
      isbn13: item.biiSalable.wIsbn13,
      format: item.biiSalable.wEditionFormat,
      numberOfPages: item.biiSalable.wNumberOfPages,
      publicationDate: item.biiSalable.wPublicationDate,
      printOnDemand: item.biiSalable.wPrintOnDemand,
      category: isSubscription
        ? i18n.t('common.subscription')
        : category?.catName,
      categoryId: category?.catId,
      maName: item.biiSalable.maName || parentNode?.maName,
      maId: item.biiSalable.maId,
      parentId: parentNode?.maId,
      parentCategoryName: parentNode?.maCategories?.find(
        (c) => c.wCatType === 'SUBJECTS'
      )?.catName,
      isTextbook,
      imageUrl,
      price: {
        value: (isDe ? prices.total.value : prices.subtotal.value) || 0,
        code: priceCode,
        units: prices.total.units || '',
      },
      tax: {
        value: prices.tax.value || 0,
      },
      totalPrice: {
        code: priceCode,
        value:
          prices.total.value || getPriceValue(prices.subtotal.value || 0, vat),
      },
      priceBeforeDiscount: qty * prices.price.value,
      licenseAgreementInfo: item.biiSalable.wProductLicenseAgreementInfo,
    };
    return orderItem;
  });
};

export const splitOn = (slicable: string, ...indices: number[]) =>
  [0, ...indices].map((n, i, m) => slicable.slice(n, m[i + 1]));

export const formatIsbn = (isbn: string) =>
  splitOn(isbn, 3, 4, 7, 12).join('-');

const getDiscount = (
  discount: OrderItemDiscounts,
  currency: string
): string => {
  const isFreeTrial = discount.discountType === DiscountType.FREETRIAL;

  if (isFreeTrial) {
    const amount = discount.freeTrialDuration?.amount || 0;
    const units = discount.freeTrialDuration?.units || TrialInterval.DAY;
    const interval = units.toLowerCase();
    const dayText = i18n.t(`common.${interval}`);
    return `${amount}-${dayText}`;
  }

  const isAmount = discount.valueType === DiscountValueType.AMOUNT;
  const discountSign = isAmount ? currency : '%';
  const discountValue = isAmount
    ? `${discountSign}${discount.discountValue}`
    : `${discount.discountValue}${discountSign}`;
  return discountValue;
};

export const convertDiscounts = (
  discounts: OrderItemDiscounts[],
  corPromoCodes: ICorPromoCodes[],
  pricPrice: PricPrice
): Discount[] => {
  const currency = getSymbolByCode(pricPrice.edges, 'SUBTOTAL');
  const coupon = corPromoCodes[0]?.prmId || '';
  return discounts.map((item) => {
    const discount = getDiscount(item, currency);
    const title = DISCOUNTS[item.discountType] || coupon;

    return {
      itemNumber: item.itemNumber,
      discount,
      title,
      freeTrialDuration: item.freeTrialDuration,
    };
  });
};

export const getSubscriptionNextBillingDate = (
  cartItems: hiConsistsOfCustomerOrderBiId[],
  discounts: Discount[]
): string | null => {
  const billingPlan = cartItems[0]?.paybBillingPlan;
  if (!billingPlan) return null;

  const isFreeTrial = discounts.some(
    (discount) => !!discount.freeTrialDuration
  );

  if (isFreeTrial) {
    const { locale } = SiteConfig.getConfig();
    const trialDiscount = discounts.find(
      (discount) => !!discount.freeTrialDuration
    );
    const amount = trialDiscount.freeTrialDuration?.amount;

    if (trialDiscount.freeTrialDuration.units === TrialInterval.DAY) {
      const nextBillDate = addDaysUTC(
        billingPlan.auAudit.createdAt.dateTime,
        amount
      );
      return dateFormatter(nextBillDate, locale);
    }

    if (trialDiscount.freeTrialDuration.units === TrialInterval.MONTH) {
      const nextBillDate = addMonthsUTC(
        billingPlan.auAudit.createdAt.dateTime,
        amount
      );
      return dateFormatter(nextBillDate, locale);
    }
  }

  const { locale } = SiteConfig.getConfig();
  const months = billingPlan.bipBillAtRPIT.ofUnit === 'MONTH' ? 1 : 12;
  const nextBillDate = addMonthsUTC(
    billingPlan.auAudit.createdAt.dateTime,
    months
  );

  return dateFormatter(nextBillDate, locale);
};
export const getSubscriptionFrequency = (
  cartItems: hiConsistsOfCustomerOrderBiId[]
): string => {
  const planName = cartItems[0]?.biiSalable?.maName?.toLowerCase() || '';
  if (
    SiteConfig.getConfig()?.isSpectraBase &&
    planName.includes('professional')
  ) {
    return 'YEAR';
  }

  const billingPlan = cartItems[0]?.paybBillingPlan;
  if (!billingPlan) return null;
  return billingPlan.bipBillAtRPIT.ofUnit;
};

export const convertDeliveryOptions = (
  deliveryOptions: DeliveryOption[]
): PreviewDeliveryOption[] => {
  return deliveryOptions.map((option) => {
    const {
      deliveryOption: { wdoCode, wdoName, wdoDescription },
      deliveryCost: { currencyCode, price },
      arrivalEstimation,
    } = option;

    const priceString = priceFormatter(price, currencyCode);

    return {
      id: wdoCode,
      title: wdoName,
      price: priceString,
      isFreeShipping: price === 0,
      date: arrivalEstimation,
      description: wdoDescription,
    };
  });
};
